import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { Modal } from './Modal';

// Promise-based replacement for window.confirm(), styled like the rest
// of the app:
//
//   const confirm = useConfirm();
//   if (await confirm({ title: 'Delete client?', danger: true })) { … }

export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

const ConfirmContext = createContext<(opts: ConfirmOptions) => Promise<boolean>>(() =>
  Promise.resolve(false),
);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback((o: ConfirmOptions) => {
    setOpts(o);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = (v: boolean) => {
    resolver.current?.(v);
    resolver.current = null;
    setOpts(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {opts && (
        <Modal title={opts.title} onClose={() => settle(false)}>
          {typeof opts.body === 'string' ? (
            <p className="muted" style={{ margin: 0 }}>
              {opts.body}
            </p>
          ) : (
            opts.body
          )}
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: '1.2rem' }}>
            <button type="button" className="btn btn-secondary" onClick={() => settle(false)}>
              {opts.cancelLabel ?? 'Cancel'}
            </button>
            <button
              type="button"
              className={`btn ${opts.danger ? 'btn-danger' : 'btn-primary'}`}
              onClick={() => settle(true)}
              autoFocus
            >
              {opts.confirmLabel ?? 'Confirm'}
            </button>
          </div>
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  return useContext(ConfirmContext);
}
